import {Box, Table, Text} from "@mantine/core";
import type {CSSProperties} from "react";
import {useTranslation} from "react-i18next";
import {useContext} from "react";
import {GPTMetaContext} from "../MetaProvider";
import {Citation} from "../inline/Citation";
import i18n from "../../../../../i18n";
import {resolvesToProffSources} from "./ChartBlock";
import shared from "./StructuredBlock.module.css";
import statsClasses from "./StatsBlock.module.css";
import detailsClasses from "./DetailsBlock.module.css";
import {FENCED_BLOCK} from "../fencedBlocks";

export type ResolvedInfoItem = {
    id?: string;
    metric?: string;
    dimension?: string;
    label: string;
    displayLabel?: string;
    value: number | null | string | {min: number; max: number};
    display?: string;
    unit?: string;
    period?: string;
    scope?: string;
    qualification?: string;
    sources?: number[];
};

export type StatsDimension = {id: string; label: string};

export type StatsMatrixRow = {
    metric: string;
    label: string;
    displayLabel?: string;
    cells: Record<string, ResolvedInfoItem | undefined>;
};

export type ResolvedInfoBlock = {
    type: "stats" | "details";
    version: 1;
    expanded: true;
    title?: string;
    note?: string;
    period?: string;
    scope?: string;
    source?: number[];
    dimensions?: StatsDimension[];
    items: ResolvedInfoItem[];
};

const isStatsValue = (value: unknown): boolean => {
    if (value === null || (typeof value === "number" && Number.isFinite(value))) return true;
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const range = value as Record<string, unknown>;
    return typeof range.min === "number" && Number.isFinite(range.min) &&
        typeof range.max === "number" && Number.isFinite(range.max) && range.min <= range.max;
};

const optionalString = (value: unknown) => value === undefined || typeof value === "string";
const sourceRefs = (value: unknown) => value === undefined || (Array.isArray(value) && value.every((ref) => Number.isInteger(ref) && (ref as number) >= 0));

/** Validates the backend-resolved, presentation-ready stats/details contract. */
export function parseResolvedInfoBlock(value: unknown): ResolvedInfoBlock | null {
    let parsed = value;
    if (typeof value === "string") {
        try { parsed = JSON.parse(value); } catch { return null; }
    }
    if (typeof parsed !== "object" || parsed === null) return null;
    const block = parsed as Record<string, unknown>;
    if ((block.type !== "stats" && block.type !== "details") || block.version !== 1 || block.expanded !== true ||
        !optionalString(block.title) || !optionalString(block.note) || !optionalString(block.period) || !optionalString(block.scope) ||
        !sourceRefs(block.source) || !Array.isArray(block.items) || block.items.length === 0) return null;
    let dimensions: StatsDimension[] | undefined;
    if (block.dimensions !== undefined) {
        if (block.type !== "stats" || !Array.isArray(block.dimensions) || block.dimensions.length === 0) return null;
        dimensions = [];
        const ids = new Set<string>();
        for (const raw of block.dimensions) {
            if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
            const dimension = raw as Record<string, unknown>;
            if (typeof dimension.id !== "string" || !dimension.id.trim() || typeof dimension.label !== "string" || !dimension.label.trim() || ids.has(dimension.id)) return null;
            ids.add(dimension.id);
            dimensions.push({id: dimension.id, label: dimension.label});
        }
    }
    const dimensionIds = new Set(dimensions?.map(({id}) => id) ?? []);
    const metricDimensions = new Set<string>();
    const valid = block.items.every((raw) => {
        if (typeof raw !== "object" || raw === null) return false;
        const item = raw as Record<string, unknown>;
        const base = typeof item.label === "string" && item.label.trim().length > 0 && optionalString(item.id) &&
            optionalString(item.metric) && optionalString(item.dimension) &&
            optionalString(item.unit) && optionalString(item.period) && optionalString(item.scope) && optionalString(item.qualification) && sourceRefs(item.sources);
        if (!base) return false;
        if (dimensions) {
            if (typeof item.metric !== "string" || !item.metric.trim() || typeof item.dimension !== "string" || !dimensionIds.has(item.dimension)) return false;
            const identity = JSON.stringify([item.metric, item.dimension]);
            if (metricDimensions.has(identity)) return false;
            metricDimensions.add(identity);
        }
        return block.type === "stats"
            ? isStatsValue(item.value) && typeof item.display === "string"
            : typeof item.value === "string";
    });
    if (!valid) return null;
    const normalized: Record<string, unknown> = {...block, ...(dimensions ? {dimensions} : {}), items: block.items.map((raw) => {
        const item = raw as Record<string, unknown>;
        return {...item, displayLabel: typeof item.displayLabel === "string" && item.displayLabel.trim() ? item.displayLabel : undefined};
    })};
    for (const key of ["title", "note", "period", "scope"] as const) {
        if (typeof normalized[key] === "string" && !normalized[key].trim()) delete normalized[key];
    }
    return normalized as unknown as ResolvedInfoBlock;
}

/** Aligns dimensioned flat stats items into first-seen metric rows and dimension columns. */
export function buildStatsMatrix(stats: ResolvedInfoBlock): StatsMatrixRow[] {
    if (!stats.dimensions?.length) return [];
    const rows = new Map<string, StatsMatrixRow>();
    for (const item of stats.items) {
        if (!item.metric || !item.dimension) continue;
        let row = rows.get(item.metric);
        if (!row) {
            row = {metric: item.metric, label: item.label, displayLabel: item.displayLabel, cells: Object.create(null) as Record<string, ResolvedInfoItem | undefined>};
            rows.set(item.metric, row);
        }
        row.cells[item.dimension] = item;
    }
    return [...rows.values()];
}

/** Keeps block attribution aligned with the renumbered citation markers in an answer. */
export function remapInfoBlockSources(content: string, mapping: Map<number, number>): string {
    return content.replace(FENCED_BLOCK, (fence, language: string, body: string) => {
        if (!/^(stats|details)[^\S\r\n]*$/.test(language)) return fence;
        const open = fence.match(/^```[^\n]*\r?\n/)?.[0] ?? "";
        const close = fence.match(/```\r?\n?$/)?.[0] ?? "```";
        const block = parseResolvedInfoBlock(body.trim());
        if (!block) return fence;
        const remap = (refs?: number[]) => refs?.map((ref) => mapping.get(ref) ?? ref);
        const closingSeparator = body.match(/\r?\n$/)?.[0] ?? "";
        const updated: ResolvedInfoBlock = {
            ...block,
            ...(block.source ? {source: remap(block.source)} : {}),
            items: block.items.map((item) => ({...item, ...(item.sources ? {sources: remap(item.sources)} : {})})),
        };
        return `${open}${JSON.stringify(updated, null, 2)}${closingSeparator}${close}`;
    });
}

const BlockCitations = ({refs}: {refs?: number[]}) => {
    const meta = useContext(GPTMetaContext);
    const indices = Array.from(new Set((refs ?? []).filter((ref) => meta?.sources?.some((source) => source.index === ref))));
    return indices.length ? <span className={shared.citations}>{indices.map((index) => <Citation key={index} reference={`[${index}]`} />)}</span> : null;
};

const InfoSkeleton = () => {
    const {t} = useTranslation();
    return <Box className={shared.skeleton} aria-busy="true" aria-label={t("structuredBlock.loading", {defaultValue: "Loading answer details"})} />;
};
const InfoUnavailable = () => {
    const {t} = useTranslation();
    return <Text size="sm" c="gray.7" className={shared.unavailable}>{t("structuredBlock.unavailable", {defaultValue: "This answer could not be displayed."})}</Text>;
};
export const StatsSkeleton = InfoSkeleton;
export const DetailsSkeleton = InfoSkeleton;
export const StatsUnavailable = InfoUnavailable;
export const DetailsUnavailable = InfoUnavailable;

const Context = ({block, item, className}: {block: ResolvedInfoBlock; item?: ResolvedInfoItem; className?: string}) => {
    const period = item ? (item.period && item.period !== block.period ? item.period : undefined) : block.period;
    const rawScope = item ? (item.scope && item.scope !== block.scope ? item.scope : undefined) : block.scope;
    const scope = infoScopeLabel(rawScope);
    const parts = [period, scope].filter((v): v is string => Boolean(v));
    return parts.length ? <Text component="span" className={className} size="xs" c="gray.7">{parts.join(" · ")}</Text> : null;
};

const ProffAttribution = ({block}: {block: ResolvedInfoBlock}) => {
    const meta = useContext(GPTMetaContext);
    const {t} = useTranslation();
    const refs = block.source?.length
        ? block.source
        : [...new Set(block.items.flatMap((item) => item.sources ?? []))];
    if (refs.length === 0 || !resolvesToProffSources(refs, meta?.rawSources ?? meta?.sources)) return null;
    return <Text size="xs" c="gray.7" className={shared.footnote}>{t("source.companyLookup")}</Text>;
};

/** Localized display text for the canonical backend scope values. */
export function infoScopeLabel(scope?: string): string | undefined {
    if (scope?.toLowerCase() === "company") return i18n.t("structuredBlock.scope.company", {defaultValue: "Company"});
    if (scope?.toLowerCase() === "group") return i18n.t("structuredBlock.scope.group", {defaultValue: "Group"});
    return scope;
}

const StatsMatrix = ({stats}: {stats: ResolvedInfoBlock}) => {
    const {t} = useTranslation();
    const dimensions = stats.dimensions ?? [];
    const rows = buildStatsMatrix(stats);
    const fontSize = dimensions.length >= 4 ? "calc(1rem * var(--mantine-scale))" : dimensions.length === 3 ? "calc(1.15rem * var(--mantine-scale))" : dimensions.length === 2 ? "calc(1.35rem * var(--mantine-scale))" : "calc(1.5rem * var(--mantine-scale))";
    const missing = t("compare.noValue");
    return <div className={statsClasses.matrixScroll}>
        <Table className={statsClasses.matrix} style={{"--stats-matrix-value-size": fontSize} as CSSProperties}>
            <Table.Thead><Table.Tr><Table.Th scope="col" className={statsClasses.matrixMetricHeader} aria-label={t("compare.view.metric", {defaultValue: "Metric"})} />{dimensions.map((dimension) => <Table.Th key={dimension.id} scope="col" className={statsClasses.matrixDimension}>{dimension.label}</Table.Th>)}</Table.Tr></Table.Thead>
            <Table.Tbody>{rows.map((row) => <Table.Tr key={row.metric}>
                <Table.Th scope="row" className={statsClasses.matrixMetric} title={row.displayLabel && row.displayLabel !== row.label ? row.label : undefined} data-metric={row.metric}>{row.displayLabel ?? row.label}</Table.Th>
                {dimensions.map((dimension) => {
                    const item = row.cells[dimension.id];
                    return <Table.Td key={dimension.id} className={item ? statsClasses.matrixCell : statsClasses.matrixMissing}>
                        {item ? <>
                            <span className={statsClasses.matrixValue}>{item.display}</span><BlockCitations refs={item.sources} />
                            <Context block={stats} item={item} className={statsClasses.matrixContext} />
                            {item.qualification && <span className={statsClasses.matrixQualification}>{item.qualification}</span>}
                        </> : <span aria-label={missing} title={missing}>—</span>}
                    </Table.Td>;
                })}
            </Table.Tr>)}</Table.Tbody>
        </Table>
    </div>;
};

export const StatsBlock = ({stats}: {stats: ResolvedInfoBlock}) => <Box className={shared.container} data-structured-block="stats">
    {(stats.title || stats.source) && <Box className={shared.header}>{stats.title && <Text component="div" fw={600} className={shared.title}>{stats.title}</Text>}<BlockCitations refs={stats.source} /></Box>}
    {(stats.period || stats.scope) && <Text component="div" size="sm" c="gray.7" className={shared.note}><Context block={stats} /></Text>}
    {stats.note && <Text component="div" size="sm" c="gray.7" className={shared.note}>{stats.note}</Text>}
    {stats.dimensions?.length ? <StatsMatrix stats={stats} /> : <div className={statsClasses.group} role="list">{stats.items.map((item, index) => <div key={item.id ?? index} className={statsClasses.item} role="listitem">
        <Text component="div" size="sm" c="gray.7" className={statsClasses.label} title={item.displayLabel && item.displayLabel !== item.label ? item.label : undefined}>{item.displayLabel ?? item.label}</Text>
        <Text component="div" className={item.value === null ? statsClasses.valueMissing : statsClasses.value}>{item.display}<BlockCitations refs={item.sources} /></Text>
        <Context block={stats} item={item} />
        {item.qualification && <Text component="div" size="sm" c="gray.7">{item.qualification}</Text>}
    </div>)}</div>}
    <ProffAttribution block={stats} />
</Box>;

export const DetailsBlock = ({details}: {details: ResolvedInfoBlock}) => <Box className={shared.container} data-structured-block="details">
    {(details.title || details.source) && <Box className={shared.header}>{details.title && <Text component="div" fw={600} className={shared.title}>{details.title}</Text>}<BlockCitations refs={details.source} /></Box>}
    {details.note && <Text component="div" size="sm" c="gray.7" className={shared.note}>{details.note}</Text>}
    {(details.period || details.scope) && <Text component="div" size="sm" c="gray.7" className={shared.note}><Context block={details} /></Text>}
    <dl className={detailsClasses.list} data-layout={details.items.some((item) => `${item.value ?? ""} ${item.qualification ?? ""}`.trim().length > 120 || String(item.value).includes("\n")) ? "rows" : "grid"}>{details.items.map((item, index) => <div key={item.id ?? index} className={detailsClasses.item}>
        <dt className={detailsClasses.label} title={item.displayLabel && item.displayLabel !== item.label ? item.label : undefined}>{item.displayLabel ?? item.label}</dt><dd className={detailsClasses.value}>{String(item.value ?? "")}<Context block={details} item={item} className={detailsClasses.context}/>
        {item.qualification && <span className={detailsClasses.qualification}>{item.qualification}</span>}<BlockCitations refs={item.sources} /></dd>
    </div>)}</dl>
    <ProffAttribution block={details} />
</Box>;
